import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { levelColor } from "../lib/gamificationApi";

/**
 * Community spotlight: top 5 contributors this month by saves received on
 * their adventures. Recognition-only — no monetisation framing.
 */
export default function TrailPioneers() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        api.get("/feed/trail-pioneers", { params: { limit: 5, period: "month" } })
            .then(({ data }) => { if (!cancel) setData(data); })
            .catch(() => { if (!cancel) setData({ items: [] }); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, []);

    if (loading) return null;
    if (!data?.items?.length) return null;

    return (
        <div
            data-testid="trail-pioneers-card"
            className="bg-snowcap border border-ridge-line rounded-lg p-5"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-ember-orange/10 flex items-center justify-center">
                    <Crown className="w-3.5 h-3.5 text-ember-orange" />
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-gray leading-none">
                        Community spotlight
                    </div>
                    <div className="font-display font-bold text-sm text-midnight-pine leading-tight mt-0.5">
                        Trail Pioneers · {data.period_label}
                    </div>
                </div>
            </div>

            <p className="text-xs text-slate-gray mb-4 leading-relaxed">
                Most-saved authors this month. Recognition for great trip reports — nothing more.
            </p>

            <ol data-testid="trail-pioneers-list" className="space-y-2.5">
                {data.items.map((row, i) => {
                    const u = row.user || {};
                    const lvl = u.level_slug;
                    return (
                        <li
                            key={u.user_id || i}
                            data-testid={`trail-pioneer-${i + 1}`}
                            className="flex items-center gap-2.5"
                        >
                            <span
                                className={`font-display font-black text-sm w-5 text-center ${
                                    i === 0 ? "text-ember-orange"
                                        : i === 1 ? "text-slate-gray"
                                        : i === 2 ? "text-trail-tan"
                                        : "text-midnight-pine/60"
                                }`}
                            >
                                {i + 1}
                            </span>
                            <Link
                                to={`/profile/${u.user_id}`}
                                className="flex items-center gap-2 flex-1 min-w-0 group"
                            >
                                <div className="w-8 h-8 rounded-full bg-trail-tan overflow-hidden flex items-center justify-center text-xs font-bold text-midnight-pine shrink-0">
                                    {u.avatar
                                        ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                                        : (u.name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold text-xs text-midnight-pine truncate group-hover:text-ember-orange transition-colors">
                                        {u.name}
                                    </div>
                                    {lvl && (
                                        <span
                                            className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-widest text-white mt-0.5"
                                            style={{ backgroundColor: levelColor(lvl) }}
                                        >
                                            {lvl.replace(/_/g, " ")}
                                        </span>
                                    )}
                                </div>
                            </Link>
                            <div className="text-right shrink-0">
                                <div className="flex items-center gap-1 text-xs font-bold text-midnight-pine">
                                    <Bookmark className="w-3 h-3 text-ember-orange" />
                                    {row.saves}
                                </div>
                                <div className="text-[9px] text-slate-gray">
                                    {row.post_count} {row.post_count === 1 ? "post" : "posts"}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ol>

            <Link
                to="/leaderboards"
                data-testid="trail-pioneers-see-all"
                className="mt-4 block text-center py-1.5 rounded-md border border-ridge-line text-slate-gray text-[11px] font-bold hover:border-midnight-pine hover:text-midnight-pine transition-all"
            >
                See all leaderboards →
            </Link>
        </div>
    );
}
